import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

// These authored leaves never move relative to the hull. Physical walls,
// machinery, target mounts, rupture leaves, fins and mouth lining stay separate.
const interiorDetail =
  /^(?:Chamber_(?:coolant_guide|coolant_manifold|structural_bronze_rib)|Throat_(?:amber_guide_light|machined_frame|roof_and_floor_flange|structural_bronze_rib)|Rear_(?:armor_captive_bolt|pressure_armor_panel))(?:_|\d|$)/;

/** Batch only static authored interior trim, retaining every indexed position,
 * normal and UV. Eight-metre hull-depth cells preserve local frustum rejection
 * rather than merging the entire interior into one always-visible draw.
 * own receives new geometry only; imported buffers/materials remain cache-owned.
 * Unexpected animated/multi-material topology fails instead of losing details. */
export function batchSkyLeviathanInterior(T, root, own) {
  root.updateWorldMatrix(true, true);
  const inverse = root.matrixWorld.clone().invert(),
    bins = new Map(),
    center = new T.Vector3(),
    batches = [];
  for (const node of root.children) {
    if (!node.isMesh || !interiorDetail.test(node.name)) continue;
    if (
      node.isSkinnedMesh ||
      node.children.length ||
      Array.isArray(node.material) ||
      node.material.transparent ||
      node.userData.skyColliderId ||
      Object.keys(node.geometry.morphAttributes).length ||
      node.geometry.groups.length
    )
      throw new Error(`Leviathan static trim cannot be batched: ${node.name}`);
    node.geometry.computeBoundingBox();
    node.geometry.boundingBox.getCenter(center).applyMatrix4(node.matrix);
    const cell = Math.floor(center.z / 8),
      key = `${node.material.uuid}:${cell}:${node.castShadow}:${node.receiveShadow}:${node.renderOrder}`;
    if (!bins.has(key)) bins.set(key, []);
    bins.get(key).push(node);
  }
  for (const nodes of bins.values()) {
    const copies = [];
    try {
      for (const node of nodes) {
        const copy = node.geometry.clone();
        copies.push(copy);
        copy.applyMatrix4(inverse.clone().multiply(node.matrixWorld));
      }
      const geometry = mergeGeometries(copies, false);
      if (!geometry) throw new Error('Leviathan static trim has incompatible attributes.');
      own(geometry);
      geometry.computeBoundingBox();
      geometry.computeBoundingSphere();
      const first = nodes[0],
        mesh = new T.Mesh(geometry, first.material);
      mesh.name = `leviathan-interior-detail-${batches.length}`;
      mesh.userData.skyBatchSources = nodes.map((node) => node.name);
      mesh.castShadow = first.castShadow;
      mesh.receiveShadow = first.receiveShadow;
      mesh.renderOrder = first.renderOrder;
      mesh.matrixAutoUpdate = false;
      root.add(mesh);
      for (const node of nodes) node.removeFromParent();
      batches.push(mesh);
    } finally {
      for (const geometry of copies) geometry.dispose();
    }
  }
  return batches;
}
